import { useCallback, useEffect, useState, type ReactNode } from 'react';
import type { ThemeRegistry } from '@classy/theme-sdk';
import { ThemeProvider } from './ThemeProvider.js';
import { getClassroomThemeKey, setClassroomThemeKey } from './themeStorage.js';
import landingBg from '../../../../assets/auth/Sunrise.png';
import styles from './SessionBg.module.css';

export interface ClassroomLandingBgProps {
  registry: ThemeRegistry;
  classroomId: string;
  /** Default theme to fall back to when this classroom has no stored choice. */
  fallbackKey?: string;
  allowedThemeKeys?: readonly string[] | undefined;
  children: ReactNode;
}

/**
 * Wraps the "classroom landing page" (ClassroomDetail config screen) in a
 * ThemeProvider driven by the per-classroom theme key — so descendants
 * like ThemePicker on the General tab still see + persist the right
 * theme — but renders the FIXED calm `assets/auth/login2-bg.png` instead
 * of the classroom's themed BgLayer.
 *
 * Sarah's rule: the landing page (hub) + the classroom landing page
 * share the `login2-bg.png` variant; the auth screens (LoginScreen +
 * SetupNotice) stay on the original `login-bg.png`. The classroom's
 * theme bg only surfaces once the teacher enters live mode (handled by
 * <ClassroomBg/> in PhaseRouter Phase 3).
 *
 * Reuses SessionBg.module.css `.bg` so the layout/z-index matches the
 * hub + auth screens exactly.
 */
export function ClassroomLandingBg({
  registry,
  classroomId,
  fallbackKey = 'jungle',
  allowedThemeKeys,
  children,
}: ClassroomLandingBgProps) {
  const [themeKey, setThemeKey] = useState(() => getClassroomThemeKey(classroomId, fallbackKey));

  useEffect(() => {
    setThemeKey(getClassroomThemeKey(classroomId, fallbackKey));
  }, [classroomId, fallbackKey]);

  const onChange = useCallback(
    (next: string) => {
      setThemeKey(next);
      setClassroomThemeKey(classroomId, next);
    },
    [classroomId],
  );

  return (
    <ThemeProvider
      registry={registry}
      themeKey={themeKey}
      fallbackKey={fallbackKey}
      allowedThemeKeys={allowedThemeKeys}
      onChange={onChange}
      setGlobalAccent
    >
      <div
        className={styles.bg}
        style={{ backgroundImage: `url(${landingBg})` }}
        aria-hidden="true"
      />
      {children}
    </ThemeProvider>
  );
}
